<script setup lang="ts">
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxRoot,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from "reka-ui";
import { computed, useTemplateRef } from "vue";

import { formatTimeSince, type ScratchpadAuthorIndex } from "@recordsure/unity-scratchpad/authors";
import type { ScratchpadAuthorsState, ScratchpadStatus } from "@recordsure/unity-scratchpad/machine";

interface Props {
  // The identityProviderId of the scratchpad shown.
  active: string;
  currentUser: { identityProviderId: string; status: ScratchpadStatus; updatedAt: string | null };
  authors: ScratchpadAuthorIndex;
  authorsStatus: ScratchpadAuthorsState["status"];
  now: number;
}

const props = defineProps<Props>();
const emit = defineEmits<{ select: [identityProviderId: string] }>();

const trigger = useTemplateRef<InstanceType<typeof PopoverTrigger>>("trigger");
defineExpose({ focus: () => (trigger.value?.$el as HTMLButtonElement | undefined)?.focus() });

function exactTime(timestamp: string | null) {
  return timestamp == null ? undefined : new Date(timestamp).toLocaleString();
}

const currentUserActive = computed(() => props.active === props.currentUser.identityProviderId);
const name = computed(() => (currentUserActive.value ? "My scratchpad" : props.active));
const updatedAt = computed(() =>
  currentUserActive.value ? props.currentUser.updatedAt : (props.authors.get(props.active)?.updated ?? null),
);
const time = computed(() => formatTimeSince(updatedAt.value, props.now));
const title = computed(() => {
  const { status } = props.currentUser;
  const message = currentUserActive.value && "message" in status ? status.message : undefined;
  const detail = message ?? (updatedAt.value == null ? undefined : `Last updated ${exactTime(updatedAt.value)}`);
  return detail == null ? name.value : `${name.value}\n${detail}`;
});
const accessibleName = computed(() =>
  currentUserActive.value
    ? "Showing my scratchpad. Change author"
    : `Showing ${props.active}'s scratchpad${time.value == null ? "" : `, last updated ${time.value}`}. Change author`,
);

const selected = computed(() => ({ id: props.active }));
</script>
<template>
  <PopoverRoot v-slot="{ open, close }">
    <PopoverTrigger
      ref="trigger"
      class="tw-inline-flex tw-h-6 tw-min-w-0 tw-max-w-full tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-gray-300 tw-bg-gray-100 tw-px-2 tw-text-xs tw-leading-4 hover:tw-bg-gray-200"
      :title="title"
      :aria-label="accessibleName"
    >
      <span class="tw-min-w-0 tw-truncate">{{ name }}</span>
      <template v-if="currentUserActive">
        <span v-if="currentUser.status.kind === 'idle'" class="tw-shrink-0 tw-whitespace-nowrap">
          ·
          <time v-if="updatedAt != null" :datetime="updatedAt">Last updated {{ time }}</time>
          <template v-else>Not saved yet</template>
        </span>
        <span v-else-if="currentUser.status.kind === 'saving'" class="tw-shrink-0 tw-whitespace-nowrap">
          · <i class="fas fa-circle-notch fa-spin tw-mr-1" aria-hidden="true"></i>Saving…
        </span>
        <span
          v-else-if="currentUser.status.kind === 'conflict'"
          class="tw-text-amber-800 tw-shrink-0 tw-whitespace-nowrap"
        >
          · <i class="fa fa-triangle-exclamation tw-mr-1" aria-hidden="true"></i>Error
        </span>
        <span v-else-if="currentUser.status.kind === 'failed'" class="tw-shrink-0 tw-whitespace-nowrap tw-text-red-700">
          · <i class="fa fa-triangle-exclamation tw-mr-1" aria-hidden="true"></i>Error
        </span>
      </template>
      <!-- An author whose scratchpad was cleared since selecting them is no longer listed, so show only their id. -->
      <span v-else-if="updatedAt != null" class="tw-shrink-0 tw-whitespace-nowrap">
        · <time :datetime="updatedAt">Last updated {{ time }}</time>
      </span>
      <i
        class="fa tw-shrink-0 tw-text-[0.625rem]"
        :class="open ? 'fa-chevron-down' : 'fa-chevron-up'"
        aria-hidden="true"
      ></i>
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverContent
        side="top"
        align="start"
        :side-offset="4"
        :collision-padding="8"
        aria-label="Scratchpad authors"
        class="tw-z-50 tw-flex tw-max-h-[min(22rem,var(--reka-popover-content-available-height))] tw-w-72 tw-max-w-[var(--reka-popover-content-available-width)] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-text-xs tw-text-black tw-shadow-xl"
      >
        <!-- Held open: the popover owns opening and closing, the combobox only searches and selects. -->
        <ComboboxRoot
          v-if="authorsStatus === 'loaded'"
          open
          :model-value="selected"
          by="id"
          :reset-search-term-on-blur="false"
          class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col"
        >
          <div class="tw-shrink-0 tw-border-b tw-border-gray-200 tw-p-1.5">
            <ComboboxInput
              auto-focus
              placeholder="Search authors…"
              aria-label="Search authors"
              class="tw-h-7 tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-2 tw-text-xs"
            />
          </div>
          <ComboboxContent aria-label="Scratchpad authors" class="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-1">
            <ComboboxItem
              v-for="author in authors.values()"
              :key="author.identityProviderId"
              :value="{ id: author.identityProviderId }"
              :text-value="author.identityProviderId"
              class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded tw-px-2 tw-py-1.5 data-[highlighted]:tw-bg-gray-100"
              @select.prevent="
                close();
                emit('select', author.identityProviderId);
              "
            >
              <span class="tw-min-w-0 tw-flex-1 tw-truncate" :title="author.identityProviderId">{{
                author.identityProviderId
              }}</span>
              <time
                v-if="author.updated != null"
                class="tw-shrink-0 tw-text-gray-500"
                :datetime="author.updated"
                :title="exactTime(author.updated)"
                >{{ formatTimeSince(author.updated, now) }}</time
              >
              <ComboboxItemIndicator class="tw-w-3 tw-shrink-0">
                <i class="fa fa-check" aria-hidden="true"></i>
              </ComboboxItemIndicator>
            </ComboboxItem>
            <ComboboxEmpty class="tw-m-0 tw-px-2 tw-py-1.5 tw-text-gray-500">
              {{
                authors.size === 0 ? "No one else has written a scratchpad for this case yet." : "No matching authors"
              }}
            </ComboboxEmpty>
          </ComboboxContent>
        </ComboboxRoot>
        <p role="status" class="tw-m-0 tw-px-3 tw-py-1.5 tw-text-gray-500 empty:tw-sr-only">
          <template v-if="authorsStatus === 'failed'">Couldn't load other scratchpads</template>
          <template v-else-if="authorsStatus === 'loading'">Loading…</template>
        </p>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>
